import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { OrganizationResponse } from "../contracts/OrganizationResponse";
import { UpdateOrganizationRequest } from "../contracts/UpdateOrganizationRequest";
import { OrganizationInvitationResponse } from "../contracts/OrganizationInvitationResponse";
import { CreateOrganizationInvitationRequest } from "../contracts/CreateOrganizationInvitationRequest";
import { OrganizationInvitationPreviewResponse } from "../contracts/OrganizationInvitationPreviewResponse";
import { AcceptInvitationRequest } from "../contracts/AcceptInvitationRequest";
import { SetVocabularyTermRequest } from "../contracts/SetVocabularyTermRequest";
import { OrganizationKind } from "../enums/OrganizationKind";
import { VocabularySlot } from "../enums/VocabularySlot";
import { OrganizationRole } from "../../instructors/enums/OrganizationRole";
import { Organization } from "../models/Organization";
import { OrganizationInvitation } from "../models/OrganizationInvitation";
import { OrganizationInvitationPreview } from "../models/OrganizationInvitationPreview";
import { OrganizationResponseMapper } from "../mappers/OrganizationResponseMapper";
import { OrganizationInvitationResponseMapper } from "../mappers/OrganizationInvitationResponseMapper";
import { enqueueVocabularyWrite } from "../utils/vocabularyWriteQueue";

const organizationMapper = new OrganizationResponseMapper();
const invitationMapper = new OrganizationInvitationResponseMapper();

/**
 * ApiErrorResponse extended with the machine-readable error code some
 * organization endpoints return (e.g. "OrganizationHasData" on accept).
 */
export interface OrganizationApiError extends ApiErrorResponse {
  code?: string;
}

const toOrganizationApiError = (
  error: unknown,
  fallbackMessage: string
): OrganizationApiError => {
  const apiError: OrganizationApiError = toApiError(error, fallbackMessage);
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (
      typeof data === "object" &&
      data !== null &&
      typeof data.code === "string"
    ) {
      apiError.code = data.code;
    }
  }
  return apiError;
};

/** Loads the signed-in instructor's organization. */
export const fetchCurrentOrganization = createAsyncThunk<
  Organization,
  void,
  { rejectValue: OrganizationApiError }
>("organization/fetchCurrent", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<OrganizationResponse>(
      "/organizations/current"
    );
    return organizationMapper.toOrganization(response.data);
  } catch (error: unknown) {
    return rejectWithValue(
      toOrganizationApiError(error, "Failed to load the organization")
    );
  }
});

/** Renames the organization (Owner/Admin). */
export const renameOrganization = createAsyncThunk<
  Organization,
  { organizationId: string; name: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/rename",
  async ({ organizationId, name }, { rejectWithValue }) => {
    try {
      const request: UpdateOrganizationRequest = { name };
      const response = await axiosInstance.put<OrganizationResponse>(
        `/organizations/${organizationId}`,
        request
      );
      return organizationMapper.toOrganization(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to rename the organization")
      );
    }
  }
);

/** Transfers ownership to another active colleague (Owner only). */
export const transferOwnership = createAsyncThunk<
  Organization,
  { organizationId: string; name: string; newOwnerId: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/transferOwnership",
  async ({ organizationId, name, newOwnerId }, { rejectWithValue }) => {
    try {
      const request: UpdateOrganizationRequest = { name, newOwnerId };
      const response = await axiosInstance.put<OrganizationResponse>(
        `/organizations/${organizationId}`,
        request
      );
      return organizationMapper.toOrganization(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to transfer ownership")
      );
    }
  }
);

/** Changes the organization's kind and / or language (Owner/Admin). */
export const updateOrganizationProfile = createAsyncThunk<
  Organization,
  { organizationId: string; name: string; kind?: OrganizationKind; languageCode?: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/updateProfile",
  async ({ organizationId, name, kind, languageCode }, { rejectWithValue }) => {
    try {
      const request: UpdateOrganizationRequest = { name, kind, languageCode };
      const response = await axiosInstance.put<OrganizationResponse>(
        `/organizations/${organizationId}`,
        request
      );
      return organizationMapper.toOrganization(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to update the organization")
      );
    }
  }
);

/** Overrides one vocabulary word for one locale (Owner/Admin). */
export const setVocabularyTerm = createAsyncThunk<
  Organization,
  { organizationId: string; locale: string; slot: VocabularySlot; one: string; other: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/setVocabularyTerm",
  async ({ organizationId, locale, slot, one, other }, { rejectWithValue }) => {
    try {
      const request: SetVocabularyTermRequest = { one, other };
      const response = await enqueueVocabularyWrite(() =>
        axiosInstance.put<OrganizationResponse>(
          `/organizations/${organizationId}/vocabulary/${locale}/${slot}`,
          request
        )
      );
      return organizationMapper.toOrganization(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to save the word")
      );
    }
  }
);

/** Resets one vocabulary word to the kind's preset (Owner/Admin). */
export const resetVocabularyTerm = createAsyncThunk<
  Organization,
  { organizationId: string; locale: string; slot: VocabularySlot },
  { rejectValue: OrganizationApiError }
>(
  "organization/resetVocabularyTerm",
  async ({ organizationId, locale, slot }, { rejectWithValue }) => {
    try {
      const response = await enqueueVocabularyWrite(() =>
        axiosInstance.delete<OrganizationResponse>(
          `/organizations/${organizationId}/vocabulary/${locale}/${slot}`
        )
      );
      return organizationMapper.toOrganization(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to reset the word")
      );
    }
  }
);

/** Loads the organization's invitations (Owner/Admin). */
export const fetchPendingInvitations = createAsyncThunk<
  OrganizationInvitation[],
  { organizationId: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/fetchPendingInvitations",
  async ({ organizationId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<OrganizationInvitationResponse[]>(
        `/organizations/${organizationId}/invitations`
      );
      return invitationMapper.toInvitations(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to load invitations")
      );
    }
  }
);

/** Invites an instructor by email (Owner/Admin). 409 = duplicate or already a member. */
export const createInvitation = createAsyncThunk<
  OrganizationInvitation,
  { organizationId: string; email: string; role: OrganizationRole },
  { rejectValue: OrganizationApiError }
>(
  "organization/createInvitation",
  async ({ organizationId, email, role }, { rejectWithValue }) => {
    try {
      const request: CreateOrganizationInvitationRequest = { email, role };
      const response = await axiosInstance.post<OrganizationInvitationResponse>(
        `/organizations/${organizationId}/invitations`,
        request
      );
      return invitationMapper.toInvitation(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to send the invitation")
      );
    }
  }
);

/** Revokes a pending invitation (Owner/Admin). */
export const revokeInvitation = createAsyncThunk<
  { invitationId: string },
  { organizationId: string; invitationId: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/revokeInvitation",
  async ({ organizationId, invitationId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(
        `/organizations/${organizationId}/invitations/${invitationId}`
      );
      return { invitationId };
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to revoke the invitation")
      );
    }
  }
);

/** Loads the anonymous invitation preview by token (works logged-out). */
export const fetchInvitationPreview = createAsyncThunk<
  OrganizationInvitationPreview,
  { token: string },
  { rejectValue: OrganizationApiError }
>(
  "organization/fetchInvitationPreview",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response =
        await axiosInstance.get<OrganizationInvitationPreviewResponse>(
          `/invitations/${encodeURIComponent(token)}`
        );
      return invitationMapper.toPreview(response.data);
    } catch (error: unknown) {
      return rejectWithValue(
        toOrganizationApiError(error, "Failed to load the invitation")
      );
    }
  }
);

/**
 * Accepts an invitation for the signed-in account. Rejects with
 * code "OrganizationHasData" (409) when the account already has studio data.
 */
export const acceptInvitation = createAsyncThunk<
  void,
  { token: string },
  { rejectValue: OrganizationApiError }
>("organization/acceptInvitation", async ({ token }, { rejectWithValue }) => {
  try {
    const request: AcceptInvitationRequest = { token };
    await axiosInstance.post("/invitations/accept", request);
  } catch (error: unknown) {
    return rejectWithValue(
      toOrganizationApiError(error, "Failed to accept the invitation")
    );
  }
});
